import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import {
  DIAM_API_PATH,
  DIAM_TEAM_ID,
  DiamPageSortField,
  getIdentitiesPage,
} from '../../diamApi';
import { useApiEssentials } from '../../../../shared/hooks/useApiEssentials';
import {
  buildIdentitiesPageFilter,
  toDiamSortFields,
} from '../../diamGridUtils';

export const IDENTITIES_FILTER = {
  teamId: DIAM_TEAM_ID,
  teamScope: 'SINGLE' as const,
};

export interface UseIdentitiesPageParams {
  page: number;
  pageSize: number;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
}

const DEFAULT_SORT: DiamPageSortField[] = [
  { field: 'createdDate', direction: 'DESC' },
];

export function useIdentities({
  page,
  pageSize,
  sortModel,
  filterModel,
}: UseIdentitiesPageParams) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials(DIAM_API_PATH);
  const filter = buildIdentitiesPageFilter(IDENTITIES_FILTER, filterModel);
  const sort = toDiamSortFields(sortModel, DEFAULT_SORT);

  return useQuery({
    queryKey: [
      'diam',
      'identities',
      'page',
      page,
      pageSize,
      sort,
      filter,
    ] as const,
    queryFn: async () => {
      const idToken = await getIdToken();
      return getIdentitiesPage(apiUrl, idToken, {
        page,
        limit: pageSize,
        sort,
        filter,
      });
    },
    enabled: isSignedIn,
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
